import Swiper from 'swiper';
import { Navigation, Pagination, Autoplay, EffectFade } from 'swiper/modules';

/**
 * SlideFade
 * 汎用フェードスライダー（Swiper・effect:fade）。KVの画像/コピー、強みパネル等で使い回す。
 * 子要素（figure / .link / .sup）があればスライド切替に合わせて順に表示する“ふわっと”演出付き。
 *
 * 複数インスタンス対応：app.js から要素ごとに `new SlideFade({ element })` で生成する。
 * pagination / navigation のセレクタは「そのスライダーの親要素」内にスコープして解決するため、
 * 同一ページに複数のフェードスライダーがあってもインジケータ等が混線しない。
 *
 * マークアップの data属性で、スライダーごとに時間を調整できる：
 *   data-slide-speed="2000"     フェード（切替）にかける時間 ms（大きいほどゆっくり）
 *   data-slide-delay="6000"     自動再生の表示間隔 ms（次へ進むまでの待機）
 *   data-slide-autoplay="false" 自動再生をオフ
 *
 * @example
 * HTML: <div class="p-kv-visual">
 *         <div class="swiper js-slide-fade">…<div class="swiper-wrapper">…</div></div>
 *         <div class="p-kv-indicator js-slide-fade-pagination"></div>
 *       </div>
 * JS: new SlideFade({ element });                       // 要素を直接渡す（複数インスタンス）
 *     new SlideFade();                                  // 既定セレクタ .js-slide-fade（先頭1件）
 *     new SlideFade({ swiperOptions: { loop: false } });
 */
export default class SlideFade {
  constructor(options = {}) {
    this.options = {
      element: null,         // DOM要素を直接指定（複数インスタンス時）。未指定なら selector を querySelector
      selector: '.js-slide-fade',
      ariaLabel: 'スライダー',
      // Swiperのデフォルトオプション
      swiperOptions: {
        modules: [Navigation, Pagination, Autoplay, EffectFade],
        effect: 'fade',
        fadeEffect: {
          crossFade: true
        },
        loop: true,
        speed: 1000,
        autoplay: {
          delay: 5000,
          disableOnInteraction: false,  // ユーザー操作後も自動再生を継続
          pauseOnMouseEnter: false,      // マウスホバーでも停止しない
          stopOnLastSlide: false,        // 最後のスライドで停止しない
          waitForTransition: true        // トランジション完了を待つ
        },
        pagination: {
          el: '.js-slide-fade-pagination',
          clickable: true,
          bulletActiveClass: 'swiper-pagination-bullet-active is-active',
          renderBullet: function (index, className) {
            return '<button type="button" class="' + className + ' bullet" aria-label="スライド ' + (index + 1) + ' へ移動"></button>';
          }
        },
        navigation: {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev'
        },
        // アクセシビリティ
        a11y: {
          prevSlideMessage: '前のスライドへ',
          nextSlideMessage: '次のスライドへ',
          firstSlideMessage: '最初のスライドです',
          lastSlideMessage: '最後のスライドです',
          paginationBulletMessage: 'スライド {{index}} へ移動'
        },
        // レスポンシブ設定
        breakpoints: {
          768: {
            // navigation: false
          }
        },
        ...options.swiperOptions
      },
      // アニメーション間隔設定
      animationIntervals: {
        figure: 100,    // 画像表示までの時間
        link: 500,      // リンク表示までの時間
        sup: 1500       // sup表示までの時間
      },
      initialAnimationDelay: 500, // 初回スライドの演出を始めるまでの待ち（マウント完了を待つ）
      ...options
    };

    this.swiper = null;
    this.element = (this.options.element instanceof HTMLElement)
      ? this.options.element
      : document.querySelector(this.options.selector);

    if (this.element) {
      this.init();
    }
  }

  /**
   * pagination / navigation のセレクタ文字列を、このスライダー単位の要素に解決する。
   * 探索範囲＝スライダー要素内 → 見つからなければ親要素内（インジケータ等は通常スライダーの兄弟）。
   * @param {string} selector
   * @returns {HTMLElement|null}
   */
  scopedEl(selector) {
    if (typeof selector !== 'string' || !this.element) return null;
    return this.element.querySelector(selector)
      || (this.element.parentElement && this.element.parentElement.querySelector(selector))
      || null;
  }

  /**
   * 初期化
   */
  init() {
    this.initSwiper();
    this.bindEvents();
    this.setupAccessibility();
    this.setupAutoplayButton();
    this.setupFollow();
    this.setupSync();
    this.setupViewportPause();
    this.setupIntegerHeight();
  }

  /**
   * スライダーの高さを整数pxに丸めて固定する。
   * aspect-ratio 由来で高さが小数（例 215.33px）になると、スライド切替時の合成レイヤーの生成/破棄で
   * 丸め位置が変わり、下のコンテンツが約1pxだけ上下にガタつく（文字が揺れて見える）ため。
   * リサイズ時はいったん解除して測り直す。
   */
  setupIntegerHeight() {
    if (!this.element) return;

    this.lastMeasuredWidth = null;

    this.applyIntegerHeight = () => {
      this.element.style.height = '';
      // 幅が変わった直後は、Swiper が各スライドに付けた幅が前の幅のまま残っている。
      // そのまま測ると前の幅で折り返した高さを固定してしまう
      if (this.swiper && this.swiper.update) this.swiper.update();
      const rect = this.element.getBoundingClientRect();
      const h = Math.round(rect.height);
      this.lastMeasuredWidth = Math.round(rect.width);
      if (h > 0) this.element.style.height = `${h}px`;
      if (this.swiper && this.swiper.update) this.swiper.update();
    };

    this.applyIntegerHeight();
    // 画像の読み込み完了で高さが確定する場合に備えて再適用
    window.addEventListener('load', this.applyIntegerHeight);

    // 幅が変わった時だけ測り直す（高さは自分で書き換えるため、高さ変化では再計算しない＝無限ループ回避）
    const remeasureIfWidthChanged = () => {
      const w = Math.round(this.element.getBoundingClientRect().width);
      if (w !== this.lastMeasuredWidth) this.applyIntegerHeight();
    };

    if ('ResizeObserver' in window) {
      this.heightObserver = new ResizeObserver(remeasureIfWidthChanged);
      this.heightObserver.observe(this.element);
    }

    // ResizeObserverと併用（片方が配信されない状況でも高さが取り残されないように）
    let timer = null;
    this.onResizeHeight = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(remeasureIfWidthChanged, 200);
    };
    window.addEventListener('resize', this.onResizeHeight);
  }

  /**
   * 画面外では自動再生を一時停止（IntersectionObserver）。
   * スクロールでスライダーが見えていない間はスライドを進めない。
   * ＝オフスクリーンのスライダー切替に伴う下方コンテンツのちらつき/再描画・無駄な処理を防ぐ。
   * 画面内に戻ったら再開（手動停止中は除く）。autoplayを持つ側（マスター）のみ対象＝追従側は自動的に止まる。
   */
  setupViewportPause() {
    if (!this.swiper || !('IntersectionObserver' in window)) return;
    if (this.element.dataset.slideAutoplay === 'false') return; // 追従側（autoplay無し）は対象外
    if (!this.swiper.autoplay) return;

    // 監視の初回コールバックを待たずに現在位置で判定する（読み込み時に画面外だと止め損ねるため）
    this.inView = this.isElementInViewport();
    this.applyAutoplayState();

    this.viewportObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        this.inView = entry.isIntersecting;
        this.applyAutoplayState();
      });
    }, { threshold: 0 });
    this.viewportObserver.observe(this.element);
  }

  isElementInViewport() {
    const r = this.element.getBoundingClientRect();
    const h = window.innerHeight || document.documentElement.clientHeight;
    const w = window.innerWidth || document.documentElement.clientWidth;
    return r.bottom > 0 && r.top < h && r.right > 0 && r.left < w;
  }

  // 画面内かつ手動停止していない時だけ自動再生する（タブ非表示時の停止はSwiper側が行う）
  applyAutoplayState() {
    if (!this.swiper || !this.swiper.autoplay) return;
    const shouldRun = this.inView && !this.manuallyPaused;
    if (shouldRun) {
      if (!this.swiper.autoplay.running) this.swiper.autoplay.start();
    } else if (this.swiper.autoplay.running) {
      this.swiper.autoplay.stop();
    }
  }

  /**
   * 追従設定：data-slide-follow="<masterセレクタ>" があれば、マスターの切替に合わせて自分も切り替える（followMaster）。
   */
  setupFollow() {
    const followSelector = this.element.dataset.slideFollow;
    if (!followSelector || !this.swiper) return;

    const master = document.querySelector(followSelector);
    if (!master) return;

    this.followMaster(master);
  }

  /**
   * 同期グループ：data-slide-sync="<group>" が同じスライダー同士を連動させる。
   * グループ内で autoplay を持つ1台がマスター、autoplay を持たない側（data-slide-autoplay="false"）が従。
   * KVの画像↔コピーのみを連動させ、他スライダー（強み等）への誤連動を防ぐための明示グルーピング。
   */
  setupSync() {
    const group = this.element.dataset.slideSync;
    if (!group || !this.swiper) return;

    // 従側（autoplay無効）だけがマスターに追従する。マスター自身は何もしない。
    if (this.element.dataset.slideAutoplay !== 'false') return;

    const master = Array.from(document.querySelectorAll(`[data-slide-sync="${group}"]`))
      .find((el) => el !== this.element && el.dataset.slideAutoplay !== 'false');
    if (!master) return;

    this.followMaster(master);
  }

  /**
   * マスターの切替開始（slidefade:slidechangestart）で、自分もマスターと同じ番号のスライドへ切り替える。
   * 「次へ」を送るだけにすると、マスターがインジケーターで飛んだりスワイプで戻ったりしたときに
   * 組がずれ、その後もずれたまま回り続ける。毎回番号で合わせ直せば、次の切替で必ず組が戻る。
   * 枚数が違うときは、マスターの番号を自分の枚数で割った余りに合わせる。
   * @param {HTMLElement} master
   */
  followMaster(master) {
    master.addEventListener('slidefade:slidechangestart', (e) => {
      const masterSwiper = e.detail && e.detail.instance && e.detail.instance.swiper;
      if (!this.swiper || !masterSwiper) return;

      const count = this.swiper.slides.length;
      if (!count) return;

      const target = masterSwiper.realIndex % count;
      if (this.swiper.realIndex !== target) {
        this.swiper.slideToLoop(target);
      }
    });
  }

  /**
   * Swiperの初期化
   */
  initSwiper() {
    try {
      // pagination / navigation のセレクタをこのスライダー単位の実DOMに差し替え（複数インスタンス混線防止）
      const opts = { ...this.options.swiperOptions };

      // data属性で個別調整（マークアップ側で上書き可能）
      //   data-slide-speed    : フェード（切替）にかける時間 ms。大きいほどゆっくり
      //   data-slide-delay    : 自動再生の表示間隔 ms（次へ進むまでの待機）
      //   data-slide-autoplay : "false" で自動再生オフ
      const ds = this.element.dataset;

      const speed = parseInt(ds.slideSpeed, 10);
      if (!Number.isNaN(speed)) {
        opts.speed = speed;
      }

      if (ds.slideAutoplay === 'false') {
        opts.autoplay = false;
      } else if (opts.autoplay) {
        const delay = parseInt(ds.slideDelay, 10);
        if (!Number.isNaN(delay)) {
          opts.autoplay = { ...opts.autoplay, delay };
        }
      }

      // 追従スライダー（data-slide-follow / data-slide-sync の従側）はマウス/フリックで動かさない（マスターに同期するため）
      if (ds.slideFollow || (ds.slideSync && ds.slideAutoplay === 'false')) {
        opts.allowTouchMove = false;
      }

      if (opts.pagination && typeof opts.pagination.el === 'string') {
        const paginationEl = this.scopedEl(opts.pagination.el);
        opts.pagination = paginationEl ? { ...opts.pagination, el: paginationEl } : false;
      }

      // 1枚のときはインジケータ非表示
      if (this.element.querySelectorAll('.swiper-slide').length <= 1) {
        opts.pagination = false;
      }

      if (opts.navigation && (typeof opts.navigation.nextEl === 'string' || typeof opts.navigation.prevEl === 'string')) {
        const nextEl = this.scopedEl(opts.navigation.nextEl);
        const prevEl = this.scopedEl(opts.navigation.prevEl);
        opts.navigation = (nextEl || prevEl) ? { ...opts.navigation, nextEl, prevEl } : false;
      }

      this.extendFirstSlide(opts);

      this.swiper = new Swiper(this.element, opts);

      // 手動停止フラグ
      this.manuallyPaused = false;

      // マウント完了後に直接初期スライドのアニメーションを開始
      setTimeout(() => {
        const activeSlide = this.element.querySelector('.swiper-slide-active');
        if (activeSlide) {
          this.startSlideAnimation(activeSlide);
        }
      }, this.options.initialAnimationDelay);

      // 初期化完了イベント
      this.element.dispatchEvent(new CustomEvent('slidefade:initialized', {
        detail: { instance: this }
      }));
    } catch (error) {
      console.error('SlideFade: Failed to initialize Swiper', error);
    }
  }

  /**
   * 1枚目だけ、表示時間にフェード時間と初回演出の待ちを足す（Swiperのスライド単位の data-swiper-autoplay）。
   * 2枚目以降の文字はフェード開始と同時に出始めるので「フェード＋表示時間」使えるが、
   * 1枚目は前のフェードが無く、読み込みと同時に表示時間を数え始めるうえ、文字も初回演出の待ちぶん遅れて出る。
   * そのままだと文字が出きる前に切り替わる。
   * 文字を持つ従スライダーを連動させるマスター（data-slide-sync）だけが対象。
   * @param {object} opts Swiperに渡すオプション
   */
  extendFirstSlide(opts) {
    if (!opts.autoplay || !this.element.dataset.slideSync) return;

    const firstSlide = this.element.querySelector('.swiper-slide');
    if (!firstSlide) return;

    const delay = opts.autoplay.delay + opts.speed + this.options.initialAnimationDelay;
    firstSlide.setAttribute('data-swiper-autoplay', String(delay));
    this.extendedSlide = firstSlide;
  }

  /**
   * イベントのバインド
   */
  bindEvents() {
    if (!this.swiper) return;

    // スライド変更時のイベント（アクティブになった時）
    this.swiper.on('slideChangeTransitionEnd', () => {
      const activeSlide = this.element.querySelector('.swiper-slide-active');
      if (activeSlide) {
        this.startSlideAnimation(activeSlide);
      }
      this.updatePaginationAria();
      this.onSlideChange(this.swiper.activeIndex, this.swiper.previousIndex);
    });

    // スライド変更開始時のイベント
    this.swiper.on('slideChangeTransitionStart', () => {
      // 1枚目の延長（extendFirstSlide）はここで外す。Swiper の once() で外すと、発火中に自分を一覧から
      // 外した拍子に後ろの handler（この処理）が飛ばされ、初回の切替でコピーへ合図が届かなかった
      if (this.extendedSlide) {
        this.extendedSlide.removeAttribute('data-swiper-autoplay');
        this.extendedSlide = null;
      }

      // 現在のアクティブスライドにis-fadeoutを付与
      const currentActiveSlide = this.element.querySelector('.swiper-slide-active');
      if (currentActiveSlide) {
        currentActiveSlide.classList.add('is-fadeout');
        this.resetSlideAnimation(currentActiveSlide);
      }
      // 入ってくる側の文字はフェード完了を待たずに出し始める。待つと画像から約2秒遅れ、表示時間が短いと読めない
      this.element.querySelectorAll('.swiper-slide').forEach((slide) => {
        slide.classList.remove('is-chars-in');
      });
      const incomingSlide = this.swiper.slides[this.swiper.activeIndex];
      if (incomingSlide) {
        incomingSlide.classList.add('is-chars-in');
      }
      // 追従スライダー（コピー等）へ「同じ瞬間に切り替えろ」と通知
      this.element.dispatchEvent(new CustomEvent('slidefade:slidechangestart', {
        detail: { instance: this }
      }));
    });

    // フェードアウトクラスをリセット
    this.swiper.on('slideChangeTransitionEnd', () => {
      const slides = this.element.querySelectorAll('.swiper-slide');
      slides.forEach(slide => {
        slide.classList.remove('is-fadeout');
      });
    });

    // 自動再生の制御（reducedMotion対応）
    this.handleReducedMotion();

    // ウィンドウリサイズ時の処理
    this.handleResize();
    
    // ハンバーガーメニューや検索フォーム開閉時の自動再生制御
    this.handleAutoplayControl();
  }

  /**
   * スライド変更時の処理
   */
  onSlideChange(newIndex, oldIndex) {
    // カスタムイベントの発火
    this.element.dispatchEvent(new CustomEvent('slidefade:slidechange', {
      detail: {
        newIndex,
        oldIndex,
        instance: this
      }
    }));
  }

  /**
   * スライドアニメーションを開始
   * @param {HTMLElement} slideElement - スライド要素
   */
  startSlideAnimation(slideElement) {
    if (!slideElement || typeof slideElement.querySelector !== 'function') {
      return;
    }

    const figure = slideElement.querySelector('figure');
    const link = slideElement.querySelector('.link');
    const sup = slideElement.querySelector('.sup');

    // 全ての要素のアニメーションクラスを一旦リセット
    const elements = slideElement.querySelectorAll('figure, .link, .sup, figcaption');
    elements.forEach(element => {
      element.classList.remove('is-start-anim');
    });

    // 文字送りアニメ発火：このスライド内の .char をCSSのstaggerで順に表示。
    // 切替時は slideChangeTransitionStart で付与済み（ここは初回表示用。付与済みなら何も変わらない）
    slideElement.classList.add('is-chars-in');

    // 画像を表示
    if (figure) {
      setTimeout(() => {
        figure.classList.add('is-start-anim');
      }, this.options.animationIntervals.figure);
    }

    // リンクを表示
    setTimeout(() => {
      if (link) {
        link.classList.add('is-start-anim');
      }
    }, this.options.animationIntervals.link);

    // supを表示（存在する場合）
    if (sup) {
      setTimeout(() => {
        sup.classList.add('is-start-anim');
      }, this.options.animationIntervals.sup);
    }
  }

  /**
   * スライドアニメーションをリセット
   * @param {HTMLElement} slideElement - スライド要素
   */
  resetSlideAnimation(slideElement) {
    if (!slideElement || typeof slideElement.querySelectorAll !== 'function') {
      return;
    }

    const elements = slideElement.querySelectorAll('figure, .link, .sup, figcaption');
    elements.forEach(element => {
      element.classList.remove('is-start-anim');
    });
  }

  /**
   * アクセシビリティ設定
   */
  setupAccessibility() {
    if (!this.element) return;

    // スライダー全体にroleとaria-label設定（マークアップ側で aria-label 指定済みならそれを優先）
    this.element.setAttribute('role', 'region');
    if (!this.element.getAttribute('aria-label')) {
      this.element.setAttribute('aria-label', this.options.ariaLabel);
    }

    // フォーカス可能要素の設定
    const slides = this.element.querySelectorAll('.swiper-slide');
    slides.forEach((slide, index) => {
      slide.setAttribute('role', 'group'); // div+aria-labelはrole必須（aria-prohibited-attr回避）
      slide.setAttribute('aria-roledescription', 'スライド');
      slide.setAttribute('aria-label', `${index + 1}`);
    });

    // ページネーションコンテナにrole="tablist"を設定
    const pagination = this.element.querySelector('.swiper-pagination');
    if (pagination) {
      pagination.setAttribute('role', 'tablist');
      pagination.setAttribute('aria-label', 'スライドページネーション');
    }

    // 初期状態のページネーションARIA属性を設定
    this.updatePaginationAria();
  }

  /**
   * ページネーションのARIA属性を更新
   */
  updatePaginationAria() {
    const bullets = this.element.querySelectorAll('.swiper-pagination-bullet');
    bullets.forEach((bullet) => {
      const isActive = bullet.classList.contains('swiper-pagination-bullet-active');
      bullet.setAttribute('aria-selected', isActive.toString());
    });
  }

  /**
   * reducedMotion設定への対応
   */
  handleReducedMotion() {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    const handleMotionPreference = (e) => {
      if (e.matches && this.swiper) {
        // アニメーションを減らす設定の場合、自動再生を停止
        this.pause();
      }
    };

    // 初期チェック
    handleMotionPreference(prefersReducedMotion);

    // 設定変更の監視
    if (prefersReducedMotion.addEventListener) {
      prefersReducedMotion.addEventListener('change', handleMotionPreference);
    }
  }

  /**
   * リサイズ処理
   */
  handleResize() {
    let resizeTimer;

    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        if (this.swiper) {
          this.swiper.update();
        }
      }, 250);
    };

    window.addEventListener('resize', handleResize, { passive: true });
  }

  /**
   * 自動再生の一時停止
   */
  pause() {
    this.manuallyPaused = true;
    if (this.swiper && this.swiper.autoplay) {
      this.swiper.autoplay.stop();
    }
  }

  /**
   * 自動再生の再開
   */
  play() {
    this.manuallyPaused = false;
    if (this.swiper && this.swiper.autoplay) {
      this.swiper.autoplay.start();
    }
  }

  /**
   * 特定のスライドへ移動
   * @param {number} index - スライドインデックス
   */
  goTo(index) {
    if (this.swiper) {
      this.swiper.slideTo(index);
    }
  }

  /**
   * 次のスライドへ
   */
  next() {
    if (this.swiper) {
      this.swiper.slideNext();
    }
  }

  /**
   * 前のスライドへ
   */
  prev() {
    if (this.swiper) {
      this.swiper.slidePrev();
    }
  }

  /**
   * 現在のスライドインデックスを取得
   * @returns {number}
   */
  getCurrentIndex() {
    return this.swiper ? this.swiper.activeIndex : -1;
  }

  /**
   * スライドの総数を取得
   * @returns {number}
   */
  getTotalSlides() {
    return this.swiper ? this.swiper.slides.length : 0;
  }

  /**
   * ハンバーガーメニューや検索フォーム開閉時の自動再生制御
   */
  handleAutoplayControl() {
    // ハンバーガーメニューのイベント監視
    document.addEventListener('hamburger:closed', () => {
      // メニューが閉じたら自動再生を再開（手動停止中でない限り）
      const autoplayButton = document.querySelector('.js-slide-fade-autoplay');
      const isManuallyPaused = autoplayButton && autoplayButton.classList.contains('is-paused');
      
      if (!isManuallyPaused && this.swiper && this.swiper.autoplay) {
        // 再生中でも安全に再開できる
        this.swiper.autoplay.start();
      }
    });

    // 検索フォームのイベント監視
    document.addEventListener('searchform:closed', () => {
      // 検索フォームが閉じたら自動再生を再開（手動停止中でない限り）
      const autoplayButton = document.querySelector('.js-slide-fade-autoplay');
      const isManuallyPaused = autoplayButton && autoplayButton.classList.contains('is-paused');
      
      if (!isManuallyPaused && this.swiper && this.swiper.autoplay) {
        // 再生中でも安全に再開できる
        this.swiper.autoplay.start();
      }
    });

    // Visibility API対応（タブが非表示になった時の制御）
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        // タブが非表示になったら一時停止
        this.pause();
      } else {
        // タブが表示されたら再開（手動で停止していない場合）
        const autoplayButton = document.querySelector('.js-slide-fade-autoplay');
        if (!autoplayButton || autoplayButton.classList.contains('is-playing')) {
          this.play();
        }
      }
    });

    // フォーカス/ブラー時の制御
    window.addEventListener('blur', () => {
      // ウィンドウがフォーカスを失っても自動再生は継続
      // 必要に応じてここで制御可能
    });

    window.addEventListener('focus', () => {
      // ウィンドウがフォーカスを得たら自動再生状態をチェック
      const autoplayButton = document.querySelector('.js-slide-fade-autoplay');
      if (this.swiper && this.swiper.autoplay && !this.swiper.autoplay.running) {
        if (!autoplayButton || autoplayButton.classList.contains('is-playing')) {
          this.play();
        }
      }
    });
  }

  /**
   * 一時停止/再生ボタンの設定
   */
  setupAutoplayButton() {
    const button = document.querySelector('.js-slide-fade-autoplay');
    if (!button || !this.swiper) return;

    // 初期状態（自動再生中）
    button.classList.add('is-playing');

    // ボタンクリックイベント
    button.addEventListener('click', (e) => {
      e.preventDefault();

      const isCurrentlyPlaying = button.classList.contains('is-playing');

      if (isCurrentlyPlaying) {
        this.pause();
        button.classList.remove('is-playing');
        button.classList.add('is-paused');
        button.setAttribute('aria-label', 'スライドショーを再生');
      } else {
        this.play();
        button.classList.remove('is-paused');
        button.classList.add('is-playing');
        button.setAttribute('aria-label', 'スライドショーを一時停止');
      }
    });

    // Swiperの自動再生イベントと同期（イベント重複を避けるためコメントアウト）
    // this.swiper.on('autoplay:play', () => {
    //   button.classList.remove('is-paused');
    //   button.classList.add('is-playing');
    //   button.setAttribute('aria-label', 'スライドショーを一時停止');
    // });

    // this.swiper.on('autoplay:pause', () => {
    //   button.classList.remove('is-playing');
    //   button.classList.add('is-paused');
    //   button.setAttribute('aria-label', 'スライドショーを再生');
    // });
  }


  /**
   * インスタンスの破棄
   */
  destroy() {
    if (this.viewportObserver) {
      this.viewportObserver.disconnect();
      this.viewportObserver = null;
    }

    if (this.onResizeHeight) {
      window.removeEventListener('resize', this.onResizeHeight);
      this.onResizeHeight = null;
    }

    if (this.heightObserver) {
      this.heightObserver.disconnect();
      this.heightObserver = null;
    }

    if (this.applyIntegerHeight) {
      window.removeEventListener('load', this.applyIntegerHeight);
      this.applyIntegerHeight = null;
    }

    if (this.swiper) {
      this.swiper.destroy();
      this.swiper = null;
    }

    // イベントの発火
    if (this.element) {
      this.element.dispatchEvent(new CustomEvent('slidefade:destroyed', {
        detail: { instance: this }
      }));
    }
  }
}
